import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import sanitize from 'rehype-sanitize';
import rehypeRaw from 'rehype-raw';
import defaultSanitizeSchema from 'hast-util-sanitize/lib/github';
import deepmerge from 'core/libs/deepmerge';
import cx from 'classnames';

import { replaceSpaces } from 'core/utils/content';

import Text from 'core/components/Text';
import Link from 'core/components/Link';
import Heading from 'core/components/Heading';

import { resolveScopedStyles } from 'core/utils/styled-jsx';

import Notes from 'core/components/Notes/';

const DEFAULT_AD_BEFORE_COUNT = 200;
const DEFAULT_AD_AFTER_COUNT = 0;
const AD_RENDERER = 'ad-renderer';
export const SITE_INJECTION = 'site-injection';

function getLastArrayItem(array) {
  if (!array.length) return undefined;
  return array[array.length - 1];
}

function getEndOffset(node) {
  return node.position.end.offset;
}

function checkAfterOffset(tree, node, after) {
  if (!after) return true;
  return after <= getEndOffset(tree) - getEndOffset(node);
}

/**
 * Переменная нужна для фолбека на отсутсвтующие ключи при траверсинге
 * по объектам.
 */
const empty = {};

const sanitizeSchema = deepmerge(
  defaultSanitizeSchema,
  {
    tagNames: [AD_RENDERER, SITE_INJECTION, 'u'],
    attributes: {
      '*': ['className'],
    },
  }
);


/**
 * Компонент отвечает за отрисовку markdown (параграфов,
 * рекламы в контенте и доп. контента, если есть необходимость).
 * @param {object} block - топик
 * @param {object} parentProps - дополнительная информация для рендера
 * @param {object} ctx - информация о расположении рекламы в топике
 * @param {function} treeItemCallback - callback для вставки дополнительного контента на сайтах
 * в treeItemCallback необходимо передать:
 * children - блоки с типом 'paragraph' и 'adRenderer'
 * tree - объект из children'ов
 * node - объект, который содержит children,  position и type
 * index - текущий index node
 * @param {element} siteInjection - react-элемент, который рендерится с помощью treeItemCallback
 * @returns {React.ReactElement}
 */


export default function markdown(block, parentProps, ctx, treeItemCallback, siteInjection) {
  const {
    attributes: {
      body,
    },
  } = block;

  const {
    isMobile,
    mobileAds,
    desktopAds,
    markdownComponents,
    theme: {
      controls: {
        topicContent: topicContentAtoms,
      },
    },
    amp,
  } = parentProps;

  const isHasContents = !!(parentProps.topic.content && parentProps.topic.content.attributes.is_has_contents);

  const breakpointAds = isMobile ? mobileAds : desktopAds;
  const adsList = [].concat(breakpointAds || []);
  const repeatableAds = adsList.filter(({ showOnlyOnce }) => !showOnlyOnce);

  if (!ctx.chars) ctx.chars = 0;
  if (!ctx.insertedAds) ctx.insertedAds = [];
  if (!ctx.renderedAdIndex) ctx.renderedAdIndex = 0;
  if (!ctx.insertedAdsPoints) ctx.insertedAdsPoints = [];

  const widgetAtoms = topicContentAtoms.markdown || empty;

  const scoped = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .text
          .heading
            margin 1.6em 0 0.5em
            clear ${topicContentAtoms.heading.clear}

            :global(.mobile) &
              margin 1.8em 0 0.5em

          :global(p)
            margin 0 auto 1em

            &:last-child
            /**
            * Убираем нижний отступ у параграфа, если за ним идет реклама,
            * за которой следует не параграф. Потому что в этом случае отступы не
            * схлопываются. Почему не параграф? Потому что у параграфа
            * нет верхнего отступа, а у других элементов есть (заголовки, списки)
            */
            &:has(+.ad + :not(p))
              margin-bottom ${amp ? '1em' : 0}

            /**
            * Добавлям списочный отступ к параграфу перед списком, т.к параграф
            * и список считается - это единая структура, параграф считается
            * заголовком списка
            */
          :global(p:has(+ :is(ul, ol)))
            margin ${topicContentAtoms.list.top}px auto 10px

          :global(li)
            margin-bottom 10px

          :global(li ul)
          :global(li ol)
            margin 10px 0

          :global(ul, ol)
            margin 0 0 ${topicContentAtoms.list.bottom}px
            padding 0 0 0 20px
      `}</style>
    </scope>
  );

  /* eslint-disable-next-line */
  const HeadingComponent = ({ level, children }) => {
    if (!children) return null;

    const text = children[0];

    return (
      <>
        <Heading
          level={level}
          className={scoped.wrapClassNames('heading')}
          {...isHasContents && { id: replaceSpaces(text) }}
        >
          {children}
        </Heading>
      </>
    );
  };

  const adInjectionPlugin = () => tree => {
    const children = [];

    tree.children.forEach((node, index) => {
      if (typeof treeItemCallback === 'function') {
        treeItemCallback(children, tree, node, index);
      }

      children.push(node);

      // Делаем подсчет длины текста на основе position для добавления рекламы в контент
      ctx.chars += node.position.end.offset - node.position.start.offset;

      // Реклама может вставать только после параграфов
      if (node.type !== 'paragraph') return;

      /**
       * Рекламные места могут выводится только один раз, а могут
       * выводиться многократно по кругу.
       *
       * Формируем список рекламных мест на основе списка
       * уже отрисованных рекламных мест.
       */
      const list = ctx.insertedAds.length >= adsList.length
        ? repeatableAds
        : adsList;

      if (!list.length) return;

      const ad = list[ctx.insertedAds.length % list.length];
      const prev = getLastArrayItem(ctx.insertedAds);

      /**
       * Определяем точку от которой будет отсчитываться длина текста
       * необходимая для определения возможности вставить
       * обрабатываемое рекламное место.
       */
      const shift = getLastArrayItem(ctx.insertedAdsPoints) || 0;

      /**
       * Вычисляем максимальное расстояние между рекламными местами
       * на случай если они пересекаются.
       */
      const before = ad.before || DEFAULT_AD_BEFORE_COUNT;
      const after = prev && prev.after || DEFAULT_AD_AFTER_COUNT;
      const indent = Math.max(before, after);

      // Условие before
      if (ctx.chars - shift >= indent) {
        /**
         * Обтекаемая реклама должна вставляться только между
         * параграфами.
         */
        if (ad.float) {
          const nextNode = tree.children[index + 1];
          if (nextNode && nextNode.type !== 'paragraph') return;
        }

        if (ctx.chars <= ctx.lastFloatWidgetPosition + 300) {
          return;
        }

        // Условие after
        if (checkAfterOffset(tree, node, ad.after)) {
          /**
           * Если все условия удовлетворены, то добавляем в документ
           * ноду с типом `adRenderer` которая в последствии будет
           * отвечать за отрисовку рекламного места.
           */
          ctx.insertedAdsPoints.push(ctx.chars);
          ctx.insertedAds.push(ad);
          /**
           * Добавляем ноду для рекламы.
           * Примерно так реализованы кастомные директивы в remark
           * https://github.com/IGassmann/remark-directive-rehype
           */
          children.push({
            type: 'element',
            data: {
              hName: AD_RENDERER,
            },
            position: node.position,
          });
        }
      }
    });

    return { ...tree, children };
  };

  return (
    <Text className={scoped.wrapClassNames('text')}>
      <Markdown
        remarkPlugins={[adInjectionPlugin, remarkGfm]}
        rehypePlugins={[rehypeRaw, [sanitize, sanitizeSchema]]}
        components={{
          a(props) {
            const { children, href, title } = props;
            if (title) {
              const noteDescription = children[0];
              return (
                <Notes
                  description={noteDescription}
                  text={title}
                  href={href}
                />
              );
            }
            return <Link to={href}>{children}</Link>;
          },

          h1: HeadingComponent,
          h2: HeadingComponent,
          h3: HeadingComponent,
          h4: HeadingComponent,
          h5: HeadingComponent,
          h6: HeadingComponent,

          [AD_RENDERER]() {
            /**
            * Получаем дескриптор рекламного места которое нужно отрисовать
            * в данном месте, формируем нужные пропсы и вызываем его
            * на отрисовку.
            */
            const index = ctx.renderedAdIndex++;

            const {
              float,
              renderer: Ad,
            } = ctx.insertedAds[index];

            return (
              <div className={cx('ad', float && 'ad--float')}>
                <style jsx>{`
                  .ad
                    :global(.lb-ad-visible)
                      clear both
                      margin-top ${topicContentAtoms.ads.top}px
                      margin-bottom ${topicContentAtoms.ads.bottom}px
                      margin-left ${-widgetAtoms.paddingLeft || 0}px
                      margin-right ${-widgetAtoms.paddingRight || 0}px

                    &--float
                      float left
                      :global(.lb-ad-visible)
                        max-width 300px
                        margin 0 10px ${topicContentAtoms.ads.bottom}px ${-widgetAtoms.paddingLeft || 0}px
                `}</style>
                <Ad index={index} />
              </div>
            );
          },

          [SITE_INJECTION]() {
            return siteInjection;
          },
          ...markdownComponents,
        }}
      >
        {body}
      </Markdown>
      <scoped.styles />
    </Text>
  );
}
